import type { ParameterGroup, Parameter } from "../../../types/ApiData";
import { useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Chip, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormControl, Grid, Input, InputLabel, Paper, Select, MenuItem, FormHelperText } from "@mui/material";
import { DynamicRules, evaluateWhen, FormValues, ValidationMessage } from "../../../utils/utils";
import { CloudDownload, InfoOutlined } from "@mui/icons-material";
//
type Error = Record<string, ValidationMessage | undefined>

export default function DynamicInputParameter({
    params,
    formValues,
    handleChange,
    panel,
    isGlobal,
    errors,
    dynamicRules,
    onlineNetworkFieldCodes = []
}: {
    params: ParameterGroup[];
    formValues: Record<string, any>;
    handleChange: (field: string, value: any) => void;
    panel: string;
    isGlobal: boolean;
    errors: Error
    dynamicRules: Record<string, DynamicRules>;
    onlineNetworkFieldCodes?: string[];
}) {
    const [expanded, setExpanded] = useState<string | false>(panel);
    const handleChangePanel =
        (panel: string) =>
            (_: React.SyntheticEvent, isExpanded: boolean) => {
                setExpanded(isExpanded ? panel : false);
            };
    const groupHasError = (group: ParameterGroup) =>
        group.parameters.some(
            param => errors[param.code]?.type === 1
        );

    const groupHasWarning = (group: ParameterGroup) =>
        group.parameters.some(
            param => errors[param.code]?.type === 2
        );

    return (
        <div>
            {params.map((group, key) => {
            const hasError = groupHasError(group);
            const hasWarning = groupHasWarning(group);
            return (
                <Accordion
                    key={group.group_code}
                    expanded={expanded === group.group_code}
                    onChange={handleChangePanel(group.group_code)}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${group.group_code}-content`}
                        id={`${group.group_code}-header`}
                    >
                        <Typography component="span" sx={{ width: '33%', flexShrink: 0,  color: hasError ? 'error.main' : hasWarning ? 'orange' : 'text.primary', }}>
                            {group.group_label}
                        </Typography>
                    </AccordionSummary>

                    <AccordionDetails>
                        <Paper elevation={0} sx={{ p: 2 }}>
                            <Grid container spacing={2} alignItems="stretch">
                                {group.parameters.map((param, key) =>
                                    renderField(
                                        param,
                                        formValues,
                                        handleChange,
                                        isGlobal,
                                        dynamicRules,
                                        errors,
                                        key,
                                        onlineNetworkFieldCodes.includes(param.code)
                                    )
                                )}
                            </Grid>
                        </Paper>
                    </AccordionDetails>
                </Accordion>
            )})}
        </div>
    );
}

// Compute dynamic field state
function getFieldState(
    fieldCode: string,
    dynamicRules: Record<string, DynamicRules>,
    formValues: FormValues
) {
    const rules = dynamicRules[fieldCode] || {};
    const visible = (rules.visible && rules.visible.length > 0) ? evaluateWhen(rules.visible, formValues) : true;
    const required = (rules.required && rules.required.length > 0) ? evaluateWhen(rules.required, formValues) : false;
    const disabled = (rules.disabled && rules.disabled.length > 0) ? evaluateWhen(rules.disabled, formValues) : false;
    const defaultValue = rules.defaultValue ?? undefined;
    const range = rules.range?.filter(r => evaluateWhen([r], formValues)) || [];
    return { visible, required, defaultValue, range, disabled };
}

function renderField(
    param: Parameter,
    formValues: FormValues,
    handleChange: (field: string, value: any) => void,
    isGlobal: boolean,
    dynamicRules: Record<string, DynamicRules>,
    errors: Error,
    key: any,
    isFromOnlineNetwork: boolean
) {

    const hasDynamicRules = Object.keys(dynamicRules).length > 0;

    const fieldState = hasDynamicRules
        ? getFieldState(param.code, dynamicRules, formValues)
        : {
            visible: true,
            required: false,
            defaultValue: undefined,
            range: undefined,
            disabled: false
        };

    const { visible, required, defaultValue, disabled } = fieldState;
    //let value = formValues[param.code] ?? defaultValue ?? param.default;
    let value = formValues[param.code] 

    
    if(!visible && value !== param.default) {
        handleChange(param.code, param.default);
    }    
    if (!visible) return null; 
    
    if(disabled && value !== param.default && param.code !== 'temperature_air') {
        handleChange(param.code, param.default);
    }

    if(!disabled && param.code === 'temperature_air' && value === null) {
         handleChange(param.code, param.default);
    }

    const isDisabled = param.status === false
        ? true
        : (disabled ?? false);

    const handleValues = (code: string, value: any, min: any, max: any) => {
        /*if (value === '') {
            handleChange(code, 0);
            return;
        } */

        handleChange(code, value);
    }

    const handleBlur = (code: string, value: any, min: any, max: any) => {
        let num = Number(value);

        if (min != null && min !== undefined) num = Math.max(min, num);
        if (max != null && max !== undefined) num = Math.min(max, num);

        handleChange(code, String(num));
    };


    // Number input
    if (param.type === 1 && ((isGlobal && param.is_global) || !isGlobal)) {
        return (
            <Grid item xs={6} key={param.code}>
            <FormControl variant="standard" fullWidth>
                <InputLabel key={key} htmlFor={param.code}>
                    {renderFieldLabel(param, isFromOnlineNetwork)}
                </InputLabel>
                <Input
                    disabled={isDisabled}
                    required={required}
                    id={param.code}
                    type="number"
                    value={value === '' ? '' : String(Number(value))}
                    key={`${key}-${param.code}`}
                    onChange={e => {
                        const intOnly = e.target.value.replace(/[.]/g, '');
                        handleValues(param.code, intOnly, param.range?.min, param.range?.max)
                        }
                    }
                    onBlur={(e) => handleBlur(param.code, e.target.value, param.range?.min, param.range?.max)}
                    inputProps={{min: param.range?.min, max: param.range}}
                />
                    {errors[param.code] && (
                        <FormHelperText style={{color: errors[param.code]?.type === 1 ? 'red' : 'orange' }}>{errors[param.code]?.message}</FormHelperText>
                    )}
            </FormControl>
            </Grid>
        );
    }

    // Select input
    if (param.type === 2 && ((isGlobal && param.is_global) || !isGlobal)) {
        return (
            <Grid item xs={6} key={param.code}>
            <FormControl variant="standard" fullWidth>
                <InputLabel id={`${param.code}-label`}>
                    {renderFieldLabel(param, isFromOnlineNetwork)}
                </InputLabel>
                <Select
                     key={`${key}-${param.code}`}
                    disabled={isDisabled}
                    labelId={`${param.code}-label`}
                    value={value}
                    onChange={e => handleChange(param.code, e.target.value)}
                >
                    {param.options?.map(opt => (
                        <MenuItem key={opt.value} value={opt.value} disabled={opt.status === false ? true : false}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
                    {errors[param.code] && (
                        <FormHelperText style={{color: errors[param.code]?.type === 1 ? 'red' : 'orange' }}>{errors[param.code]?.message}</FormHelperText>
                    )}
            </FormControl>
            </Grid>
        );
    }

    if (param.type === 3 && ((isGlobal && param.is_global) || !isGlobal)) {
        return (
            <Grid item xs={6} key={param.code}>
                <FormControl variant="standard" fullWidth>
                    <InputLabel htmlFor={param.code}>
                        {renderFieldLabel(param, isFromOnlineNetwork)}
                    </InputLabel>

                    <Input
                        key={`${key}-${param.code}`}
                        disabled={isDisabled}
                        required={required}
                        id={param.code}
                        type="number"
                        value={value === '' ? '' : String(Number(value))}
                        onChange={e =>
                            handleValues(
                                param.code,
                                e.target.value,
                                param.range?.min,
                                param.range?.max
                            )
                        }
                        onBlur={(e) => handleBlur(param.code, e.target.value, param.range?.min, param.range?.max)}
                        inputProps={{
                            min: param.range?.min,
                            max: param.range?.max,
                            step: 'any',     // 
                        }}
                    />

                    {errors[param.code] && (
                        <FormHelperText style={{color: errors[param.code]?.type === 1 ? 'red' : 'orange' }}>{errors[param.code]?.message}</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        );
    }

    return null;
}

const renderFieldLabel = (
    param: Parameter,
    isFromOnlineNetwork: boolean
) => (
    <>
        {param.label}
        {param.unit && ` (${param.unit})`}

        {isFromOnlineNetwork && (
            <Tooltip arrow title={'From Network Loader'}>
                <span className="m-1"><CloudDownload color="success" fontSize='small' /></span>
            </Tooltip>
        )}

        {param.hint && (
            <Tooltip arrow title={param.hint}>
                <InfoOutlined
                    sx={{
                        color: "#2196f3",
                        ml: 0.5,
                        fontSize: 18,
                        verticalAlign: "middle"
                    }}
                />
            </Tooltip>
        )}
    </>
);

